import i18n from "../i18n";

function getLocale(): string {
  const lang = i18n.language;
  if (lang?.startsWith("es")) return "es-MX";
  return "en-US";
}

export function formatDate(
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(getLocale(), options ?? { month: "long", day: "numeric", year: "numeric" });
}

export function formatDateShort(date: string | Date | null | undefined): string {
  return formatDate(date, { month: "short", day: "numeric" });
}

export function formatMonthYear(date: string | Date | null | undefined): string {
  return formatDate(date, { month: "long", year: "numeric" });
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return "0";
  return value.toLocaleString(getLocale());
}

export function formatCompact(value: number | null | undefined): string {
  if (value == null) return "0";
  // Always use en-US compact notation (K/M/B) — universal for social-media
  // engagement metrics, regardless of UI language.
  return Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(value);
}

export function formatCurrency(value: number, currency = "USD"): string {
  return new Intl.NumberFormat(getLocale(), { style: "currency", currency, minimumFractionDigits: 0 }).format(value);
}

/**
 * Smart date for content surfaces (cards, table rows, detail header):
 * - < 1 minute → "just now"
 * - < 1 hour → "Nm ago"
 * - < 24 hours → "Nh ago"
 * - < 7 days → "Nd ago"
 * - < 4 weeks → "Nw ago"
 * - older → short absolute with year, e.g. "Apr 9, 2026"
 */
export function formatDateSmart(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";

  const diffMs = Date.now() - d.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (diffMs < 0) {
    return formatDate(date, { month: "short", day: "numeric", year: "numeric" });
  }

  const locale = getLocale();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (minutes < 1) return rtf.format(0, "minute");
  if (hours < 1) return rtf.format(-minutes, "minute");
  if (days < 1) return rtf.format(-hours, "hour");
  if (days < 7) return rtf.format(-days, "day");
  if (weeks < 4) return rtf.format(-weeks, "week");
  return formatDate(date, { month: "short", day: "numeric", year: "numeric" });
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const locale = getLocale();
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (days > 0) return rtf.format(-days, "day");
  if (hours > 0) return rtf.format(-hours, "hour");
  if (minutes > 0) return rtf.format(-minutes, "minute");
  return rtf.format(-seconds, "second");
}
